import type { CloudTier } from '@ipfabric/utils/license/baseCloudConstruct';
import { useState } from 'react';

/**
 * The design derives chart shades from three colours plus a semi-opaque overlay. That overlay is the
 * `--semantic-colors-tint-*` token, which is white in the light theme and black in the dark one - a
 * tint always moves a colour towards the surface. `base + tint-NN` is therefore the same thing as
 * `base` drawn at `1 - NN` alpha over the card, which is what these ramps do: the chart canvas is
 * transparent, so the alpha composites over the card exactly like the DOM swatches beside it.
 *
 * Using alpha rather than the numbered ramps (`brand-blue-400`, `-300`, ...) matters only in the dark
 * theme, where those steps get lighter - away from the surface - while a tint has to get darker.
 */
const CHART_BASE_TOKENS = {
  /** `brand-blue-500` / `-200` - on-premise devices and Tier A. */
  brand: '--semantic-colors-surface-brand',
  /** `brand-green-500` / `-200` - Tier B. */
  green: '--semantic-colors-surface-element-primary',
  /** `gray-400` / `gray-100` - Tier C, which is non-billable. */
  gray: '--semantic-colors-text-plain',
} as const;

type ChartBase = keyof typeof CHART_BASE_TOKENS;

/** Five device types plus the aggregated "Other" slice, darkest first. */
const DEVICE_ALPHAS = [1, 0.8, 0.6, 0.45, 0.3, 0.15];

/** Segments within one tier bar, darkest at the base of the bar. */
const TIER_ALPHAS = [1, 0.75, 0.5];

const TIER_BASES: Record<CloudTier, ChartBase> = {
  A: 'brand',
  B: 'green',
  C: 'gray',
};

/** Visible fallback, so a renamed token shows up instead of silently dropping a slice. */
const FALLBACK_RGB = '109 114 123';

export type ChartPalette = {
  /** Slice colours for the device breakdown, in legend order. */
  device: string[];
  /** Segment colours per tier, from the base of the bar upwards. */
  tier: Record<CloudTier, string[]>;
};

/**
 * Tokens hold their colour as `R G B` for Tailwind's `rgb(var(--token) / <alpha-value>)`. This turns
 * one into the comma form of `rgba()`, which every browser and jsdom's CSS parser accept - the
 * space-separated form is silently dropped in tests, and the two render identically.
 */
const readTokenRgb = (style: CSSStyleDeclaration, token: string): string => {
  const value = style.getPropertyValue(token).trim();
  return (value === '' ? FALLBACK_RGB : value).split(/\s+/).join(', ');
};

export const resolveChartPalette = (
  style: CSSStyleDeclaration,
): ChartPalette => {
  const rgbByBase = Object.fromEntries(
    Object.entries(CHART_BASE_TOKENS).map(([base, token]) => [
      base,
      readTokenRgb(style, token),
    ]),
  ) as Record<ChartBase, string>;

  const shades = (base: ChartBase, alphas: number[]): string[] =>
    alphas.map((alpha) => `rgba(${rgbByBase[base]}, ${alpha})`);

  return {
    device: shades('brand', DEVICE_ALPHAS),
    tier: {
      A: shades(TIER_BASES.A, TIER_ALPHAS),
      B: shades(TIER_BASES.B, TIER_ALPHAS),
      C: shades(TIER_BASES.C, TIER_ALPHAS),
    },
  };
};

/**
 * Resolved once per mount: switching the theme reloads the page, so the tokens cannot change
 * underneath a mounted chart.
 */
export const useChartPalette = (): ChartPalette => {
  const [palette] = useState(() =>
    resolveChartPalette(getComputedStyle(document.documentElement)),
  );
  return palette;
};
